import { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import ReactGA from "react-ga4";
import {
  Box, Flex, Grid, Skeleton, Text,
  useColorModeValue,
} from "@chakra-ui/react";
import ErrorCard from '../components/common/ErrorCard';
import PageLayout, { PageHeader } from '../components/layout/PageLayout';
import { USE_MOCK_SHOPS, getMockListsIndex } from '../data/mockShops';
import { TYPE } from "../styles/theme";

const API_BASE = process.env.REACT_APP_SERVER_BASE_URL?.replace(/\/$/, "") || "http://localhost:3001";

export default function ListsPage() {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  const surface       = useColorModeValue("#fdfaf5", "#1e1510");
  const border        = useColorModeValue("rgba(0,0,0,0.08)", "rgba(255,255,255,0.07)");
  const accent        = useColorModeValue("#8b3e12", "#c8a97e");
  const text          = useColorModeValue("#1a0c04", "#e8d9c4");
  const dim           = useColorModeValue("#6a4020", "#a89880");
  const pillBg        = useColorModeValue("rgba(139,62,18,0.08)", "rgba(200,169,126,0.10)");
  const pillBorder    = useColorModeValue("rgba(139,62,18,0.30)", "rgba(200,169,126,0.30)");
  const badgeBg       = useColorModeValue("#fdfaf5", "#241910");

  useEffect(() => {
    ReactGA.send({
      hitType: "pageview",
      page: window.location.pathname,
      title: "collections",
      location: window.location.href,
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    setFetchError(false);
    (async () => {
      try {
        if (USE_MOCK_SHOPS) {
          setLists(getMockListsIndex());
          return;
        }
        const res = await fetch(`${API_BASE}/lists`);
        if (!res.ok) throw new Error(`${res.status}`);
        setLists(await res.json());
      } catch {
        setFetchError(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [refetchTrigger]);

  const gridCols = { base: "1fr", md: "repeat(2, 1fr)" };

  return (
    <PageLayout transparent pt={{ base: "92px", md: "140px" }}>
      <PageHeader
        title="Collections"
        subtitle="Hand-picked selections of the world's best independent coffee shops, sourced from trusted publications and competitions."
      />

      {loading ? (
        <Box maxW="820px">
          <Grid templateColumns={gridCols} gap={{ base: 5, md: 6 }}>
            {[1, 2].map((i) => (
              <Skeleton key={i} borderRadius="18px" h="180px" startColor={surface} endColor={border} />
            ))}
          </Grid>
        </Box>
      ) : fetchError ? (
        <ErrorCard
          eyebrow="Couldn't load"
          heading="The collections didn't brew."
          body="Something went wrong loading the collections. Give it another go."
          action={{ label: 'Try again', onClick: () => setRefetchTrigger(t => t + 1) }}
        />
      ) : lists.length === 0 ? (
        <ErrorCard
          eyebrow="Coming soon"
          heading="Nothing here yet."
          body="Collections are hand-curated and added gradually. Check back soon."
        />
      ) : (
        <Box maxW="820px">
          <Grid templateColumns={gridCols} gap={{ base: 5, md: 6 }}>
            {lists.map((list) => (
              <Box
                key={list.id}
                as={RouterLink}
                to={`/collections/${list.slug}`}
                onClick={() => {
                  ReactGA.event({
                    category: "Collections",
                    action: "Open Collection",
                    label: list.slug || list.title,
                  });
                }}
                bg={surface}
                border="1px solid"
                borderColor={border}
                borderRadius="18px"
                p={{ base: 6, md: 7 }}
                transition="transform 0.15s, border-color 0.15s"
                _hover={{ transform: "translateY(-2px)", borderColor: accent, textDecoration: "none" }}
                display="flex"
                flexDirection="column"
                minH={{ md: "200px" }}
                boxShadow="0 18px 40px -22px rgba(60,30,10,0.3)"
              >
                <Flex justify="space-between" align="flex-start" mb={4} gap={3}>
                  <Text
                    as="h2"
                    fontFamily="heading"
                    fontSize={TYPE.blockTitle}
                    fontWeight={400}
                    color={text}
                    flex={1}
                    lineHeight={1.25}
                  >
                    {list.title}
                  </Text>
                  <Box
                    bg={badgeBg}
                    color={accent}
                    border="1px solid"
                    borderColor={pillBorder}
                    borderRadius="full"
                    px="10px"
                    py="3px"
                    fontSize={TYPE.meta}
                    fontWeight={600}
                    flexShrink={0}
                    letterSpacing="0.02em"
                  >
                    {list.entry_count}
                  </Box>
                </Flex>

                {list.description && (
                  <Text fontSize={TYPE.body} color={dim} noOfLines={3} mb={5} lineHeight={1.7}>
                    {list.description}
                  </Text>
                )}

                <Flex justify="space-between" align="center" mt="auto">
                  {list.source_name ? (
                    list.source_url ? (
                      <Box
                        as="span"
                        role="link"
                        tabIndex={0}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          window.open(list.source_url, "_blank", "noopener,noreferrer");
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            e.stopPropagation();
                            window.open(list.source_url, "_blank", "noopener,noreferrer");
                          }
                        }}
                        display="inline-flex"
                        alignItems="center"
                        px="10px"
                        py="4px"
                        fontSize={TYPE.meta}
                        fontWeight={500}
                        color={accent}
                        bg={pillBg}
                        border="1px solid"
                        borderColor={pillBorder}
                        borderRadius="full"
                        cursor="pointer"
                        _hover={{ bg: pillBorder }}
                        transition="background 0.12s"
                      >
                        {list.source_name} ↗
                      </Box>
                    ) : (
                      <Text fontSize={TYPE.meta} color={dim}>
                        {list.source_name}
                      </Text>
                    )
                  ) : (
                    <Box />
                  )}

                  <Text fontSize={TYPE.meta} color={dim}>
                    View →
                  </Text>
                </Flex>
              </Box>
            ))}
          </Grid>
        </Box>
      )}
    </PageLayout>
  );
}
